import {
  AllowedHandleTypeComponent,
  CoreApi,
  Product,
  ProductInfoComponent,
  ProductPartial,
  ProductType
} from '@inter-ikea-storageone/range';
import { BestaOpeningFunctionComponent, NotSupportLeg } from '@inter-ikea-storageone/range-besta';
import { ComponentIds, Entity, HierarchyUtils } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { getEntityConnectorsThatOffer } from 'rangeConfig/shared/utils/connectors';

const isBestaTvBench = (type: string) => {
  return (
    type === ProductType.BESTA_TV_BENCH_1200_ASM || type === ProductType.BESTA_TV_BENCH_1800_ASM
  );
};

const isBestaFrame = (type: string) => {
  return type === ProductType.BESTA_FRAME_600_ASM || type === ProductType.BESTA_FRAME_1200_ASM;
};

export const matchTopPanelWidth = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => {
  if (isBestaTvBench(selectedProduct.type)) {
    return (
      product.communicatedDimensions.width === selectedProduct.communicatedDimensions.width &&
      product.type === ProductType.BESTA_TOP_PANEL_TV_BENCH
    );
  } else if (isBestaFrame(selectedProduct.type)) {
    return (
      product.communicatedDimensions.width === selectedProduct.communicatedDimensions.width &&
      product.type === ProductType.BESTA_TOP_PANEL
    );
  } else if (
    selectedProduct.type === ProductType.BESTA_TOP_PANEL ||
    selectedProduct.type === ProductType.BESTA_TOP_PANEL_TV_BENCH
  ) {
    return (
      product.communicatedDimensions.width === selectedProduct.communicatedDimensions.width &&
      product.type === selectedProduct.type
    );
  } else {
    return false;
  }
};

export const matchTopPanelDepth = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => {
  if (isBestaTvBench(selectedProduct.type)) {
    return (
      product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth &&
      product.type === ProductType.BESTA_TOP_PANEL_TV_BENCH
    );
  } else if (isBestaFrame(selectedProduct.type)) {
    return (
      product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth &&
      product.type === ProductType.BESTA_TOP_PANEL
    );
  } else if (
    selectedProduct.type === ProductType.BESTA_TOP_PANEL ||
    selectedProduct.type === ProductType.BESTA_TOP_PANEL_TV_BENCH
  ) {
    return (
      product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth &&
      product.type === selectedProduct.type
    );
  } else {
    return false;
  }
};

export const matchExactDrawerOpeningFunction = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product) => {
  if (!selectedEntity?.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)) {
    return true;
  }
  const openingType = selectedEntity?.getComponent<BestaOpeningFunctionComponent>(
    BestaOpeningFunctionComponent.id
  )?.openingFunction;

  if (openingType && !!product.parts.find(part => part?.product?.type === openingType)) {
    return true;
  } else {
    return false;
  }
};

export const matchDrawerOpeningFunction = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product) => {
  if (!selectedEntity?.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)) {
    return true;
  }
  const selectedHandleProductTypes =
    selectedEntity?.getComponent<AllowedHandleTypeComponent>(AllowedHandleTypeComponent.id)
      ?.handleTypes || [];

  if (product.meta?.allowedHandleTypes && product.meta?.allowedHandleTypes.length === 0) {
    // If product has included handle it should always have soft closing runner
    return !!product.parts.find(part => part?.product?.type === ProductType.BESTA_SOFT_CLOSING);
  } else if (selectedHandleProductTypes.length === 0) {
    // If selected product has included handle (soft closing) it should show
    // the other drawers with push to open functionallity
    return !!product.parts.find(part => part?.product?.type === ProductType.BESTA_PUSH_OPEN);
  }
  return matchExactDrawerOpeningFunction(coreApi, selectedProduct, selectedEntity)(product);
};

export const matchFrontLayoutToFrame = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product) => {
  if (!selectedEntity) return false;
  const frame = HierarchyUtils.findWithComponentInUpwardsHierarchy(
    selectedEntity,
    ComponentIds.FRAME_COMPONENT_ID
  );
  if (!frame) return false;
  const frameSize = frame.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;
  return (
    product.communicatedDimensions.width === 600 &&
    product.communicatedDimensions.height === frameSize?.height &&
    product.communicatedDimensions.depth === frameSize?.depth
  );
};

export const supportLegs = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product) => {
  const requiresSupport = ['20293592', '10293597', '50293595'];
  if (requiresSupport.includes(selectedProduct.coreProductId)) {
    return false;
  }

  if (selectedEntity && selectedEntity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
    const frameLegs = HierarchyUtils.descendants(selectedEntity).filter(
      child =>
        child.hasComponent(ComponentIds.LEG_COMPONENT_ID) &&
        child.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType !==
          ProductType.BESTA_SUPPORTING_LEG
    );
    if (frameLegs.length > 0) {
      const legItemNo = coreApi.entities.getGlobalArticleNumber(frameLegs[0]);

      return (
        product.type === ProductType.BESTA_SUPPORTING_LEG ||
        (legItemNo === product.globalItemNo && !frameLegs[0].hasComponent(NotSupportLeg.id))
      );
    }
  }

  return (
    product.type === ProductType.BESTA_SUPPORTING_LEG ||
    selectedProduct.coreProductId === product.coreProductId
  );
};

/**
 * Checks if organizer, normaly or rotated, fits shelf depth
 */
export const matchShelfDepth = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product) => {
  if (!selectedEntity) return false;
  const shelfDepth = coreApi.entities.getEntityComSize(selectedEntity)?.depth || 0;
  return (
    product.communicatedDimensions.depth < shelfDepth ||
    product.communicatedDimensions.width < shelfDepth
  );
};

export const matchCanHaveLight = (
  _coreApi: CoreApi,
  _selectedProduct: Product,
  selectedEntity?: Entity
) => (_product: Product) => {
  if (!selectedEntity) return false;

  const connectors = getEntityConnectorsThatOffer(selectedEntity, 'inner-light');
  return !!connectors.length;
};
